import React, { useState, useEffect, useMemo } from "react"; // Added useEffect
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faUtensils,
  faCalendarAlt,
  faTruck,
} from "@fortawesome/free-solid-svg-icons";
import cateringData from "../db/data/cateringData";
import { applyOverridesToData } from "../utils/priceOverrides";
import { usePriceOverrides } from "../context/PriceOverridesContext";
import "./CateringPage.css";

// Moved CateringTray outside CateringPage for better structure
const CateringTray = ({ tray }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div className="catering-tray" id={tray.id}>
      <div className="catering-tray-content">
        <div className="tray-header">
          <h3>{tray.name}</h3>
          <div className="tray-price">
            {tray.price && <span className="price">${tray.price}</span>}
            {tray.priceDescription && (
              <span className="price-description">{tray.priceDescription}</span>
            )}
            {tray.priceUnit && (
              <span className="price-unit">{tray.priceUnit}</span>
            )}
          </div>
        </div>

        <div className="tray-details">
          {tray.image && (
            <div className="tray-image-frame">
              {imageError ? (
                <div className="image-coming-soon">
                  <FontAwesomeIcon icon={faUtensils} />
                  <span>Image Coming Soon</span>
                </div>
              ) : (
                <img
                  src={tray.image}
                  alt={tray.name}
                  className="tray-image"
                  onError={() => setImageError(true)}
                  loading="lazy"
                />
              )}
            </div>
          )}

        <div className="tray-description">
          {tray.items && (
            <ul className="tray-items">
              {tray.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          )}

          {tray.description && (
            <p className="description">{tray.description}</p>
          )}

          {tray.notes && <p className="notes">{tray.notes}</p>}

          {tray.addons && tray.addons.length > 0 && (
            <div className="tray-addons">
              {tray.addons.map((addon, index) => (
                <div key={index} className="addon">
                  <span className="addon-name">ADD {addon.name}</span>
                  {addon.price && (
                    <span className="addon-price">
                      ${addon.price} {addon.priceUnit}
                    </span>
                  )}
                  {addon.description && (
                    <span className="addon-description">
                      {addon.description}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};

// Moved CateringNavBar outside CateringPage
const CateringNavBar = ({ activeSection, scrollToSection }) => (
  <div className="catering-nav">
    <div className="container">
      <ul className="catering-nav-list">
        <li>
          <button
            className={activeSection === "trays" ? "active" : ""}
            onClick={() => scrollToSection("trays")}
          >
            Catering Trays
          </button>
        </li>
        <li>
          <button
            className={activeSection === "boxed-lunches" ? "active" : ""}
            onClick={() => scrollToSection("boxed-lunches")}
          >
            Boxed Lunches
          </button>
        </li>
        <li>
          <button
            className={activeSection === "salad-bowls" ? "active" : ""}
            onClick={() => scrollToSection("salad-bowls")}
          >
            Salad Bowls
          </button>
        </li>
      </ul>
    </div>
  </div>
);

const CateringPage = () => {
  const [activeSection, setActiveSection] = useState("trays"); // Default to first section
  const sectionRefs = useMemo(() => ({
    trays: React.createRef(),
    "boxed-lunches": React.createRef(),
    "salad-bowls": React.createRef(),
  }), []);

  const { overrides } = usePriceOverrides();

  // Apply price overrides from the master override list
  const dataWithOverrides = useMemo(() => {
    return applyOverridesToData("cateringData", cateringData, overrides);
  }, [overrides]);

  // Scroll to section when clicking nav link
  const scrollToSection = (sectionId) => {
    // setActiveSection(sectionId); // Let IntersectionObserver handle this
    const section = sectionRefs[sectionId].current;
    if (section) {
      // Calculate offset to account for sticky nav height (adjust value as needed)
      const navHeight = 80; // Example height, adjust based on actual nav height
      const sectionTop =
        section.getBoundingClientRect().top + window.pageYOffset - navHeight;

      window.scrollTo({ top: sectionTop, behavior: "smooth" });
    }
  };

  // Intersection Observer for scroll spy
  useEffect(() => {
    const observerOptions = {
      root: null, // relative to document viewport
      rootMargin: "-100px 0px -50% 0px", // Adjust margins: top offset, trigger earlier on bottom
      threshold: 0, // Trigger as soon as any part enters/leaves margin
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe each section
    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    // Cleanup observer on component unmount
    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [sectionRefs]); // Re-run effect if refs change (shouldn't happen often)

  return (
    <div className="catering-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1 className="force-revue">{dataWithOverrides.intro.title}</h1>
          <p className="subtitle">{dataWithOverrides.intro.subtitle}</p>

          <div className="call-notice">
            <h3>
              <FontAwesomeIcon icon={faPhone} /> Call to Place Your Catering
              Order
            </h3>
            <p>
              Our catering specialists will help you create the perfect menu for
              your event.
            </p>
            <a href="tel:248-352-7377" className="btn primary">
              248-352-7377
            </a>
          </div>
        </div>
      </section>

      {/* Menu Categories Navigation - Pass props */}
      <CateringNavBar
        activeSection={activeSection}
        scrollToSection={scrollToSection}
      />

      {/* Minimum Notice */}
      <div className="minimum-notice">
        <div className="minimum-notice-content"> {/* Changed class name */}
          <div className="notice-icon">
            <FontAwesomeIcon icon={faCalendarAlt} />
          </div>
          <p>{dataWithOverrides.intro.notes}</p>
        </div>
      </div>

      {/* Catering Trays Section */}
      <section
        id="trays"
        className="catering-section"
        ref={sectionRefs["trays"]}
      >
        <div className="container">
          <h2 className="section-title force-revue">Catering Trays</h2>
          <p className="section-description">
            Perfect for office meetings, celebrations, and gatherings
          </p>

          <div className="catering-trays-grid">
            {dataWithOverrides.trays.map((tray) => (
              <CateringTray key={tray.id} tray={tray} />
            ))}
          </div>
        </div>
      </section>

      {/* Boxed Lunches Section */}
      <section
        id="boxed-lunches"
        className="catering-section"
        ref={sectionRefs["boxed-lunches"]}
      >
        <div className="container">
          <h2 className="section-title force-revue">Boxed Lunches</h2>
          <p className="section-description">
            Individual meals, perfect for meetings and events
          </p>

          {/* Use CateringTray component for Boxed Lunches */}
          <div className="boxed-lunch-container">
            <CateringTray tray={dataWithOverrides.boxedLunches} />
          </div>
        </div>
      </section>

      {/* Salad Bowls Section */}
      <section
        id="salad-bowls"
        className="catering-section"
        ref={sectionRefs["salad-bowls"]}
      >
        <div className="container">
          <h2 className="section-title force-revue">Salad Bowls</h2>
          <p className="section-description">{dataWithOverrides.saladBowls.note}</p>

          {/* Use CateringTray component for Salad Bowls */}
          <div className="catering-trays-grid">
            {dataWithOverrides.saladBowlItems.map((salad) => (
              <CateringTray key={salad.id} tray={salad} />
            ))}
          </div>
        </div>
      </section>

      {/* Order Info */}
      <section className="catering-info">
        <div className="container">
          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
              <h3>Advance Notice</h3>
              <p>Please allow at least 24 hours notice for catering orders.</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faTruck} />
              </div>
              <h3>Delivery Available</h3>
              <p>Delivery available for orders over $50. Charge dependent on location.</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <h3>Call to Order</h3>
              <p>Speak with our well-trained catering specialists at 248-352-7377. We can find a solution for any event, guaranteed.</p>
            </div>
          </div>

          <div className="footnotes">
            {dataWithOverrides.cateringFootnotes && dataWithOverrides.cateringFootnotes.length > 0 && (
              <p className="footnote">
                {dataWithOverrides.cateringFootnotes[0]} {/* Assuming the first footnote is the asterisk explanation */}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Place Your Catering Order?</h2>
          <p>
            Our catering specialists are ready to help you create the perfect
            menu for your event.
          </p>
          <a href="tel:248-352-7377" className="btn primary">
            Call 248-352-7377
          </a>
        </div>
      </section>
      <div className="food-safety-disclaimer">
        <div className="container">
          <p>*CONSUMING RAW OR UNDERCOOKED MEATS, POULTRY, SEAFOOD, SHELLFISH, OR EGGS MAY INCREASE YOUR RISK OF FOODBORNE ILLNESS, ESPECIALLY IF YOU HAVE CERTAIN MEDICAL CONDITIONS.</p>
        </div>
      </div>
    </div>
  );
};

export default CateringPage;
